import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyToJob } from '../../services/applyService';
import axiosClient from '../../services/axiosClient';
import Swal from 'sweetalert2';

 function hasValidSession() {
   try {
     const raw = window.localStorage.getItem('userDetails');
     if (!raw) return false;
     const data = JSON.parse(raw);
     if (!data?.token) return false;
     if (data?.expireDate) {
       const exp = new Date(data.expireDate);
       if (!Number.isNaN(exp.getTime()) && new Date() > exp) return false;
     }
     return true;
   } catch (_) {
     return false;
   }
 }

const ConsultantApply = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const id = searchParams.get('id');
  const [message, setMessage] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [useExistingResume, setUseExistingResume] = useState(true);
  const [resumes, setResumes] = useState([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [selectedResumeId, setSelectedResumeId] = useState('');

  useEffect(() => {
    if (!hasValidSession()) {
      const target = `/consultant/job-apply?id=${encodeURIComponent(id || '')}`;
      navigate(`/login?redirect=${encodeURIComponent(target)}`, { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingResumes(true);
        const res = await axiosClient.get('/resumes', {
          params: { belongLoggedUser: 1, perPage: 10 },
          headers: { Accept: 'application/json' },
        });
        const data = res.data;
        const root = data?.result || data || {};
        let list = root?.data || data?.data || [];
        if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
        const mapped = (Array.isArray(list) ? list : []).map((r) => ({
          id: String(r.id),
          name: r.name || 'Liasse',
          filename: r.filename || r.name || 'Liasse',
          url: r.url || r.file_url || r.download_url || r.filepath || r.path || '',
        })).filter(r => r.id && (r.name || r.filename));
        if (!mounted) return;
        setResumes(mapped);
        if (mapped.length > 0) {
          setSelectedResumeId(mapped[0].id);
          setUseExistingResume(true);
        } else {
          setUseExistingResume(false);
        }
      } catch (e) {
        // en cas d'erreur on laisse uniquement l'upload manuel
        if (mounted) {
          setResumes([]);
          setUseExistingResume(false);
        }
      } finally {
        if (mounted) setLoadingResumes(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!id) {
      alert("Offre introuvable.");
      return;
    }
    const trimmed = message.trim();
    if (!trimmed) {
      alert("Veuillez saisir un message au recruteur.");
      return;
    }
    if (trimmed.length < 20) {
      alert("Votre message doit contenir au moins 20 caractères.");
      return;
    }
    if (!useExistingResume && !cvFile) {
      alert("Veuillez joindre votre liasse (PDF).");
      return;
    }
    if (useExistingResume && !selectedResumeId) {
      alert("Veuillez sélectionner une liasse existante ou choisir l'option d'upload.");
      return;
    }
    try {
      setSending(true);
      setError('');
      let fileToSend = cvFile;

      // Si l'utilisateur choisit une liasse existante, télécharger le fichier pour le renvoyer
      if (useExistingResume && selectedResumeId) {
        const chosen = resumes.find(r => r.id === selectedResumeId);
        if (!chosen || !chosen.url) {
          throw new Error("Liasse sélectionnée introuvable.");
        }
        const resp = await fetch(chosen.url, { credentials: 'omit' });
        if (!resp.ok) {
          throw new Error("Impossible de récupérer la liasse sélectionnée.");
        }
        const blob = await resp.blob();
        const filename = chosen.name || 'cv.pdf';
        fileToSend = new File([blob], filename, { type: blob.type || 'application/pdf' });
      }

      await applyToJob({
        postId: id,
        body: trimmed,
        resumeFile: fileToSend,
      });

      Swal.fire({
        icon: 'success',
        title: 'Candidature envoyée !',
        text: 'Votre candidature a été envoyée avec succès.',
        confirmButtonColor: '#3085d6',
        confirmButtonText: 'OK'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate(-1);
        }
      });
    } catch (err) {
      console.error('[ConsultantApply] apply error:', err);
      const msg = err?.response?.data?.message || err?.message || 'Erreur lors de la soumission de votre candidature';
      setError(msg);
      Swal.fire({
        icon: 'error',
        title: 'Erreur !',
        text: msg,
        confirmButtonColor: '#d33',
        confirmButtonText: 'OK'
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container py-4">
      <button className="btn btn-link mb-3" onClick={() => navigate(-1)}>
        ← Retour
      </button>
      <div className="card border-0 shadow-sm" style={{ borderRadius: 16 }}>
        <div className="card-body p-4">
          <h3 className="mb-1">Candidater à l'offre</h3>
          <div className="text-muted mb-3">Référence: {id}</div>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <form onSubmit={onSubmit}>
            <div className="mb-3">
              <label className="form-label">Message au recruteur</label>
              <textarea className="form-control" rows={6} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Votre message..." />
            </div>
            <div className="mb-3">
              <label className="form-label">Votre Liasse</label>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="radio"
                  name="resume_mode"
                  id="resume-existing"
                  checked={useExistingResume}
                  onChange={() => setUseExistingResume(true)}
                />
                <label className="form-check-label" htmlFor="resume-existing">
                  Utiliser une liasse existante
                </label>
              </div>
              {useExistingResume && (
                <div className="mt-2">
                  {loadingResumes ? (
                    <div className="text-muted">Chargement de vos liasses…</div>
                  ) : resumes.length === 0 ? (
                    <div className="text-muted">Aucune liasse trouvée. Vous pouvez en ajouter dans l'onglet "Mes Liasses".</div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedResumeId}
                      onChange={(e) => setSelectedResumeId(e.target.value)}
                    >
                      {resumes.map(r => (
                        <option key={r.id} value={r.id}>{r.filename}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}
              <div className="form-check mt-3">
                <input
                  className="form-check-input"
                  type="radio"
                  name="resume_mode"
                  id="resume-upload"
                  checked={!useExistingResume}
                  onChange={() => setUseExistingResume(false)}
                />
                <label className="form-check-label" htmlFor="resume-upload">
                  Joindre une nouvelle Liasse (PDF)
                </label>
              </div>
              {!useExistingResume && (
                <div className="mt-2">
                  <input
                    type="file"
                    className="form-control"
                    accept="application/pdf"
                    onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                  />
                </div>
              )}
            </div>
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-success" disabled={sending}>
                {sending ? 'Envoi…' : 'Soumettre la candidature'}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConsultantApply;
